import { AxiosError } from 'axios'
import type { CancelToken } from 'axios'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'

import type {
  VcnBulkExecuteConflictBody,
  VcnBulkExecuteRequest,
  VcnBulkExecuteResponse,
} from './dto/vcn-bulk-execute.dto'
import { VcnSortOrder } from '../components/VcnManagement/types'
import { VcnBulkExecuteConflictError } from './dto/vcn-bulk-execute.dto'
import type { VcnBulkJobStatus } from './dto/vcn-bulk-job.dto'
import type { GetEmployeeVcnCardsResponse } from './dto/employee-vcn-cards.dto'
import type {
  VcnCardActionItem,
  VcnCardActionRequest,
  VcnCardActionResponse,
} from './dto/vcn-card-action.dto'
import type {
  GetVcnManagementCardListRequestDto,
  GetVcnManagementCardListResponseDto,
} from './dto/vcn-management-card-list.dto'
import type {
  GetVcnManagementListRequestDto,
  GetVcnManagementListResponseDto,
} from './dto/vcn-management-list.dto'
import type { VcnMigrationSummary } from './dto/vcn-migration-summary.dto'
import type { VcnCardMetrics } from './dto/vcn-card-metrics.dto'
import type {
  VcnCardBulkExecuteRequest,
  VcnCardBulkPreviewRequest,
  VcnCardBulkPreviewResponse,
} from './dto/vcn-card-bulk.dto'
import {
  parseContentDispositionFilename,
  saveBlobToDisk,
} from '../utils/blob-download'
import type {
  VcnBulkUploadPreviewRequest,
  VcnBulkUploadPreviewResponse,
} from './dto/vcn-bulk-upload-preview.dto'
import type {
  VcnSelectionRequest,
  VcnSelectionResponse,
} from './dto/vcn-selection.dto'
import type { VirtualCardType } from 'modules/virtual-cards/constants'

function buildListSearchParams(
  request: GetVcnManagementListRequestDto
): URLSearchParams {
  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize),
  })

  if (request.search?.trim()) {
    params.set('search', request.search.trim())
  }
  if (request.carrierName) {
    params.set('carrierName', request.carrierName)
  }
  if (request.vcnProvider) {
    params.set('vcnProvider', request.vcnProvider)
  }
  if (request.vcnStatus) {
    params.set('vcnStatus', request.vcnStatus)
  }
  if (request.primaryPaymentMethod) {
    params.set('primaryPaymentMethod', request.primaryPaymentMethod)
  }
  for (const key of request.clientPublicKeys ?? []) {
    params.append('clientPublicKeys', key)
  }
  if (request.dashboardMetric) {
    params.set('dashboardMetric', request.dashboardMetric)
  }

  return params
}

export async function getVcnManagementCarriers(): Promise<string[]> {
  const response = await apiClient.get<string[]>('vcn-management/carriers')
  return response.data
}

export async function getVcnMigrationSummary(): Promise<VcnMigrationSummary> {
  const response = await apiClient.get<VcnMigrationSummary>(
    'vcn-management/migration-summary'
  )
  return response.data
}

/**
 * Card Mode's tiles. A separate endpoint from `migration-summary`, which counts
 * benefits for Benefit Mode's tiles — the two must not be swapped, since a
 * card-unit number under a benefit-unit tile is the mismatch this revamp exists
 * to remove.
 */
export async function getVcnCardMetrics(): Promise<VcnCardMetrics> {
  const response = await apiClient.get<VcnCardMetrics>(
    'vcn-management/card-metrics'
  )
  return response.data
}

/**
 * Card Mode's params, deliberately built separately from the benefit list's.
 *
 * The two endpoints differ in three: `cardStatus` here vs `vcnStatus` there
 * (in Card Mode it is the card's own status), `inUse` is Card Mode only, and
 * `primaryPaymentMethod` / `dashboardMetric` do not exist here. Sharing one
 * builder would silently send params `/cards` does not accept — and with
 * `forbidNonWhitelisted` off, an unrecognised filter is ignored rather than
 * rejected, which is how a mistyped `vcnStatus` once returned 17,488 rows
 * against a tile counting 10,919.
 */
/** Bounds a hung export; a slow one is expected and must not be cut off. */
const CARD_EXPORT_TIMEOUT_MS = 300_000

function buildCardFilterParams(
  request: Omit<GetVcnManagementCardListRequestDto, 'page' | 'pageSize'>
): URLSearchParams {
  const params = new URLSearchParams()

  if (request.search?.trim()) {
    params.set('search', request.search.trim())
  }
  if (request.vcnProvider) {
    params.set('vcnProvider', request.vcnProvider)
  }
  if (request.cardStatus) {
    params.set('cardStatus', request.cardStatus)
  }
  for (const value of request.inUse ?? []) {
    params.append('inUse', value)
  }
  for (const key of request.clientPublicKeys ?? []) {
    params.append('clientPublicKeys', key)
  }
  if (request.carrierName) {
    params.set('carrierName', request.carrierName)
  }

  return params
}

/** The grid's request: the same filters, plus the page it wants. */
function buildCardListSearchParams(
  request: GetVcnManagementCardListRequestDto
): URLSearchParams {
  const params = buildCardFilterParams(request)
  params.set('page', String(request.page))
  params.set('pageSize', String(request.pageSize))
  // Only when a column is actually sorted — no params means newest first, which
  // is a distinct state from "sorted by created date".
  if (request.sort) {
    params.set('sort', request.sort)
    params.set('order', request.order ?? VcnSortOrder.ASC)
  }
  return params
}

/**
 * Downloads the card list as a CSV file.
 *
 * Sends the filters and no paging: the file is every matching row, and the
 * endpoint ignores `page`/`pageSize` precisely so a stray one cannot hand back a
 * quietly truncated file. Shares `buildCardFilterParams` with the grid, so the
 * rows in the file are selected by the same param names the screen used.
 *
 * The server names the file; `Content-Disposition` is readable here only because
 * EPS also sends `Access-Control-Expose-Headers`.
 */
/**
 * Resolves an uploaded file before anything is terminated.
 *
 * Sends only what the file holds. Everything else — the card key, the benefit
 * using the card — is resolved by EPS, both here and again when the job runs,
 * because a binding can change in between.
 */
export async function previewVcnCardBulk(
  request: VcnCardBulkPreviewRequest
): Promise<VcnCardBulkPreviewResponse> {
  const response = await apiClient.post<VcnCardBulkPreviewResponse>(
    'vcn-management/cards/bulk-preview',
    request
  )
  return response.data
}

/**
 * Queues the terminate job. Separate from the benefit-keyed `bulk-execute`: that
 * one resolves each benefit's PRIMARY card, which is not the card an uploaded
 * token names.
 */
export async function executeVcnCardBulk(
  request: VcnCardBulkExecuteRequest
): Promise<VcnBulkExecuteResponse> {
  try {
    const response = await apiClient.post<VcnBulkExecuteResponse>(
      'vcn-management/cards/bulk-execute',
      request,
      // EPS answers 202 Accepted; axios would otherwise treat that as an error.
      { validateStatus: (status) => status === 202 || status === 200 }
    )
    return response.data
  } catch (error) {
    throw toBulkExecuteError(error)
  }
}

export async function exportVcnManagementCards(
  request: Omit<GetVcnManagementCardListRequestDto, 'page' | 'pageSize'>,
  cancelToken?: CancelToken
): Promise<{ fileName: string }> {
  const response = await apiClient.get<Blob>('vcn-management/cards/export', {
    params: buildCardFilterParams(request),
    // Without this axios parses the body as JSON and the file arrives corrupt.
    responseType: 'blob',
    // An unfiltered export enriches every card server-side and can run for tens
    // of seconds. There is no client default, so this only bounds a hung
    // request rather than a slow one.
    timeout: CARD_EXPORT_TIMEOUT_MS,
    // Lets the caller drop the request when it stops caring about the answer —
    // the tab is unmounted on a tab switch, and an abandoned export would go on
    // enriching every card server-side for nothing.
    //
    // A cancelToken, not an AbortSignal: this app is on axios 0.17, whose XHR
    // adapter has no notion of `signal` and ignores it silently. Same approach
    // as CensusReview's get-in-review-page-data.
    cancelToken,
  })
  const fileName =
    parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined
    ) ?? 'vcn-cards.csv'
  saveBlobToDisk(response.data, fileName)
  return { fileName }
}

/**
 * One row per VCN card, both providers, all card statuses. `provider` and
 * `inUse` arrive already resolved by EPS, so unlike the benefit list there is
 * no response remapping here.
 */
export async function getVcnManagementCardList(
  request: GetVcnManagementCardListRequestDto
): Promise<GetVcnManagementCardListResponseDto> {
  const response = await apiClient.get<GetVcnManagementCardListResponseDto>(
    'vcn-management/cards',
    { params: buildCardListSearchParams(request) }
  )
  return response.data
}

export async function getVcnManagementList(
  request: GetVcnManagementListRequestDto
): Promise<GetVcnManagementListResponseDto> {
  const response = await apiClient.get<{
    items: (Omit<
      GetVcnManagementListResponseDto['items'][number],
      'provider'
    > & {
      provider?: string
      vcnProvider?: string
    })[]
    total: number
  }>('vcn-management', { params: buildListSearchParams(request) })
  return {
    total: response.data.total,
    items: response.data.items.map(({ vcnProvider, provider, ...row }) => ({
      ...row,
      // Fall back on empty string too (not just null/undefined): a blank
      // provider from the API should defer to the vcnProvider field.
      provider: provider || vcnProvider || '',
    })),
  }
}

export async function getEmployeeVcnCards(
  employeePublicKey: string
): Promise<GetEmployeeVcnCardsResponse> {
  const response = await apiClient.get<GetEmployeeVcnCardsResponse>(
    `vcn-management/employees/${encodeURIComponent(employeePublicKey)}/cards`
  )
  return response.data
}

// Terminate is the only card-keyed action (by VirtualCardKey). EPS resolves the
// owning employee from each card's bound benefit, so no employee key is sent.
// Terminate & New and Create reuse the benefit-keyed grid endpoints below.
export async function terminateEmployeeVcnCards(
  cards: VcnCardActionItem[]
): Promise<VcnCardActionResponse> {
  const body: VcnCardActionRequest = { cards }
  const response = await apiClient.post<VcnCardActionResponse>(
    'vcn-management/cards/terminate',
    body
  )
  return response.data
}

export async function previewVcnBulkUpload(
  body: VcnBulkUploadPreviewRequest
): Promise<VcnBulkUploadPreviewResponse> {
  const response = await apiClient.post<VcnBulkUploadPreviewResponse>(
    'vcn-management/bulk-preview',
    body
  )
  return response.data
}

/**
 * Turns a 409 from either bulk-execute endpoint into a typed conflict.
 *
 * Only one bulk job runs platform-wide — benefit-keyed and card-keyed share that
 * constraint — so both paths can be refused by a job the admin did not start,
 * and both need to name the job that is blocking them.
 */
function toBulkExecuteError(error: unknown): unknown {
  const axiosError = error as AxiosError
  if (axiosError.response?.status === 409) {
    const data = axiosError.response.data as
      | VcnBulkExecuteConflictBody
      | undefined
    if (data?.jobId) {
      return new VcnBulkExecuteConflictError({
        message: data.message || 'A bulk VCN job is already queued or running',
        jobId: data.jobId,
      })
    }
  }
  return error
}

export async function executeVcnBulkActions(
  body: VcnBulkExecuteRequest
): Promise<VcnBulkExecuteResponse> {
  try {
    const response = await apiClient.post<VcnBulkExecuteResponse>(
      'vcn-management/bulk-execute',
      body,
      { validateStatus: (status) => status === 202 || status === 200 }
    )
    return response.data
  } catch (error) {
    throw toBulkExecuteError(error)
  }
}

export async function getVcnBulkJobCurrent(): Promise<VcnBulkJobStatus | null> {
  const response = await apiClient.get<VcnBulkJobStatus>(
    'vcn-management/bulk-jobs/current',
    {
      validateStatus: (status) => status === 200 || status === 204,
    }
  )
  return response.status === 204 ? null : response.data
}

export async function dismissVcnBulkJobCurrent(): Promise<void> {
  await apiClient.post('vcn-management/bulk-jobs/current/dismiss', undefined, {
    validateStatus: (status) => status === 204 || status === 200,
  })
}

export async function terminateVcnSelected(
  benefitIds: string[]
): Promise<VcnSelectionResponse> {
  const body: VcnSelectionRequest = { benefitIds }
  const response = await apiClient.post<VcnSelectionResponse>(
    'vcn-management/terminate',
    body
  )
  return response.data
}

export async function terminateAndNewVcnSelected(
  benefitIds: string[],
  provider?: VirtualCardType
): Promise<VcnSelectionResponse> {
  const body: VcnSelectionRequest = { benefitIds, provider }
  const response = await apiClient.post<VcnSelectionResponse>(
    'vcn-management/terminate-and-new',
    body
  )
  return response.data
}

export async function createNewVcnSelected(
  benefitIds: string[],
  provider?: VirtualCardType
): Promise<VcnSelectionResponse> {
  const body: VcnSelectionRequest = { benefitIds, provider }
  const response = await apiClient.post<VcnSelectionResponse>(
    'vcn-management/create-new',
    body
  )
  return response.data
}

export async function fetchAllVcnManagementRows(
  request: Omit<GetVcnManagementListRequestDto, 'page' | 'pageSize'>,
  maxRows?: number
): Promise<GetVcnManagementListResponseDto['items']> {
  const PAGE_SIZE = 200
  let page = 1
  const allRows: GetVcnManagementListResponseDto['items'] = []

  let response = await getVcnManagementList({
    ...request,
    page,
    pageSize: PAGE_SIZE,
  })
  allRows.push(...response.items)

  const canFetchMore = (): boolean => {
    const hasFullPage = response.items.length === PAGE_SIZE
    const belowTotal =
      typeof response.total !== 'number' || allRows.length < response.total
    const belowMaxRows = maxRows == null || allRows.length < maxRows
    return hasFullPage && belowTotal && belowMaxRows
  }

  while (canFetchMore()) {
    page++
    response = await getVcnManagementList({
      ...request,
      page,
      pageSize: PAGE_SIZE,
    })
    allRows.push(...response.items)
  }

  return maxRows != null ? allRows.slice(0, maxRows) : allRows
}

export function isVcnBulkExecuteConflictError(
  error: unknown
): error is VcnBulkExecuteConflictError {
  return error instanceof VcnBulkExecuteConflictError
}
